/**
 * GreenSyn Inventory — GST reference data and GSTIN validation.
 *
 * GSTIN is the identifier every Indian tax invoice is filed against, and a wrong
 * one is not a cosmetic problem: it invalidates the invoice and the input credit
 * with it. So this module does more than check the length — it validates the
 * **check digit**, which catches the typo that a format check happily accepts.
 *
 * The checksum is the standard GSTIN one: the first 14 characters are weighted
 * 1,2,1,2…, each product is folded to its base-36 digit sum, and the 15th
 * character is whatever makes the total a multiple of 36. Verified against the
 * worked example in the GST references (and, in the tests, against mutations of
 * it — which is the property that actually matters).
 *
 * Nothing here computes tax. These are reference fields for a supplier record:
 * what is on their certificate, so that a purchase order and the certificate can
 * be compared by eye.
 */

/**
 * GST state codes: the first two digits of every GSTIN, and the key to where a
 * supply is taxed from.
 *
 * Two entries are historical and deliberately kept. A GSTIN is forever: 25
 * (Daman and Diu) and 28 (Andhra Pradesh) were superseded in reorganisations, but
 * registrations issued under them are still live, and rejecting one would turn
 * away a real supplier. See `GST_SUPERSEDED_STATE_CODES`.
 *
 * This is *reference* data, not a closed set the code reasons about — nothing
 * branches on a state — so membership is validated at the edge rather than by a
 * database CHECK. Adding a state is a one-line change, not a migration.
 */
export const GST_STATE_CODES = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
  '31',
  '32',
  '33',
  '34',
  '35',
  '36',
  '37',
  '38',
  '97',
  '99',
] as const;

export type GstStateCode = (typeof GST_STATE_CODES)[number];

export const GST_STATES: Record<GstStateCode, string> = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '25': 'Daman and Diu (superseded by 26)',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh (superseded by 37)',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory',
  '99': 'Centre Jurisdiction',
};

/**
 * Codes that are no longer issued but still appear on live registrations. Kept
 * as data so the UI can say which code replaced which, rather than presenting a
 * supplier's certificate as simply "wrong".
 */
export const GST_SUPERSEDED_STATE_CODES: Partial<Record<GstStateCode, GstStateCode>> = {
  '25': '26',
  '28': '37',
};

export function gstStateName(code: string | null | undefined): string | null {
  if (!code) return null;
  return GST_STATES[code as GstStateCode] ?? null;
}

/**
 * The shape of a GSTIN: two state digits, the ten-character PAN, the entity
 * number, a character reserved for future use, and the check digit.
 *
 * Deliberately permissive about the last three characters — the reserved
 * character is not always 'Z' for bodies like UN agencies, and the checksum is
 * what actually decides whether a GSTIN is well-formed. Being stricter here
 * would reject valid suppliers without catching anything the checksum misses.
 */
export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][0-9A-Z]{2}$/;

const BASE36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The character a GSTIN must end with, given its first 14.
 *
 * Exported so a validator can say what the check digit *should* be: "invalid"
 * on a 15-character string is not actionable, but "…should end in V" points at
 * the typo.
 */
export function gstinCheckCharacter(first14: string): string | null {
  let sum = 0;
  let weight = 1;

  for (const character of first14) {
    const value = BASE36.indexOf(character);
    if (value < 0) return null;

    const product = value * weight;
    // Base-36 folding: each product contributes its own digit sum, so a wrapped
    // value counts once for the carry and once for the remainder.
    sum += Math.floor(product / 36) + (product % 36);
    weight = weight === 1 ? 2 : 1;
  }

  return BASE36[(36 - (sum % 36)) % 36] ?? null;
}

/** Upper-cased and trimmed, because a GSTIN is conventionally written that way. */
export function normaliseGstin(value: string): string {
  return value.trim().toUpperCase();
}

export function isValidGstin(value: string): boolean {
  const gstin = normaliseGstin(value);
  if (!GSTIN_PATTERN.test(gstin)) return false;
  return gstinCheckCharacter(gstin.slice(0, 14)) === gstin[14];
}

/**
 * The state a GSTIN is registered in, or null if it is not well-formed.
 *
 * This is why the state field is safe to store: it is not a second opinion about
 * where the supplier is, it is what their certificate says, and the two are
 * required to agree.
 */
export function gstStateCodeFromGstin(gstin: string): GstStateCode | null {
  const code = normaliseGstin(gstin).slice(0, 2);
  return (GST_STATE_CODES as readonly string[]).includes(code) ? (code as GstStateCode) : null;
}

/* -------------------------------------------------------------------------- */
/* Cross-field rules                                                           */
/* -------------------------------------------------------------------------- */

export interface SupplierGstInput {
  gstin?: string | null;
  gstRegistrationType?: string | null;
  gstStateCode?: string | null;
}

export interface GstIssue {
  /** Stable, so the web app can attach it to the right field. */
  field: 'gstin' | 'gstRegistrationType' | 'gstStateCode';
  message: string;
}

/**
 * The rules that only make sense once the fields are read together.
 *
 * They are checked against the **merged** record rather than the request body,
 * because a partial update cannot be judged on its own: sending
 * `{gstRegistrationType: 'regular'}` is legal or not depending on whether the
 * supplier already has a GSTIN. Both the API and the database enforce these
 * (the database cannot check a checksum in a CHECK, so it takes the shape and
 * the relationships).
 *
 * An unset registration type means "not recorded yet", which is not the same as
 * "unregistered" — the first is honest about a supplier you are still
 * onboarding, and only the second forbids a GSTIN.
 */
export function supplierGstIssues(input: SupplierGstInput): GstIssue[] {
  const issues: GstIssue[] = [];
  const gstin = input.gstin ? normaliseGstin(input.gstin) : null;
  const type = input.gstRegistrationType ?? null;
  const stateCode = input.gstStateCode ?? null;

  if (type === 'unregistered' && gstin) {
    issues.push({
      field: 'gstin',
      message: 'An unregistered supplier cannot have a GSTIN — clear the GSTIN or set the type',
    });
  }

  if ((type === 'regular' || type === 'composition') && !gstin) {
    issues.push({
      field: 'gstin',
      message: `A ${type === 'regular' ? 'regular' : 'composition'} supplier must have a GSTIN — leave the type blank if it is not known yet`,
    });
  }

  if (gstin && stateCode) {
    const fromGstin = gstStateCodeFromGstin(gstin);
    if (fromGstin && fromGstin !== stateCode) {
      issues.push({
        field: 'gstStateCode',
        message: `The GSTIN is registered in ${gstStateName(fromGstin)} (${fromGstin}), not ${gstStateName(stateCode) ?? stateCode} (${stateCode})`,
      });
    }
  }

  return issues;
}
